import type { CSSProperties } from 'react'
import type { Order } from '@/types'
import type { OrderTypeConfig } from '@/lib/orderTypeSettings'
import { colorForOrderTypeId, sanitizeOrderTypeHexColor } from '@/lib/orderTypeSettings'
import { cn } from '@/lib/utils'

/**
 * Retail ticket lifecycle:
 *
 *   empty → staging → [Charge] ready_to_pay → partial_paid → closed
 *                   ↘ [Hold]   parked
 *   parked + cart → adding
 *
 * A parked sale is an unpaid `pending` order with items — there are no empty
 * orders, open tabs or kitchen fires in the retail model.
 */
export type TicketLifecycle =
  | 'empty'
  | 'staging'
  | 'parked'
  | 'adding'
  | 'ready_to_pay'
  | 'partial_paid'
  | 'closed'

export interface TicketLifecycleInput {
  order: Order | null | undefined
  cartCount: number
  checkoutOpen: boolean
  payableRemaining: number
}

export interface TicketLifecycleMeta {
  /** Machine value */
  state: TicketLifecycle
  /** Short label shown as pill (e.g. "Parked", "Adding items") */
  label: string
  /** One-line sub-label under the pill */
  helper: string
  /** Visual tone for the pill */
  tone: 'neutral' | 'amber' | 'emerald' | 'violet' | 'sky' | 'slate'
}

/** Count non-voided lines already placed on the order. */
export function countPlacedItems(order: Order | null | undefined): number {
  if (!order?.items?.length) return 0
  return order.items.filter((i) => i.status !== 'voided').length
}

/** Central lifecycle resolver for the right rail ticket. */
export function getTicketLifecycle(input: TicketLifecycleInput): TicketLifecycleMeta {
  const { order, cartCount, checkoutOpen, payableRemaining } = input
  const placed = countPlacedItems(order)
  const hasCart = cartCount > 0

  if (checkoutOpen && order) {
    if (payableRemaining <= 0) {
      return {
        state: 'closed',
        label: 'Closed',
        helper: 'Fully paid',
        tone: 'slate',
      }
    }
    const anyPaid = (order.payments ?? []).some((p) => p.status === 'completed')
    if (anyPaid) {
      return {
        state: 'partial_paid',
        label: 'Partial',
        helper: 'Awaiting remaining balance',
        tone: 'violet',
      }
    }
    return {
      state: 'ready_to_pay',
      label: 'Ready to pay',
      helper: 'Choose tender and complete payment',
      tone: 'violet',
    }
  }

  if (!order && !hasCart) {
    return {
      state: 'empty',
      label: 'Empty',
      helper: 'Add items to start a sale',
      tone: 'neutral',
    }
  }

  if (order && hasCart) {
    return {
      state: 'adding',
      label: 'Adding items',
      helper: 'New items join the sale on Add items',
      tone: 'sky',
    }
  }

  if (!order && hasCart) {
    return {
      state: 'staging',
      label: 'Staging',
      helper: 'Items not yet placed',
      tone: 'sky',
    }
  }

  if (order && !hasCart) {
    return {
      state: 'parked',
      label: 'Parked',
      helper:
        placed > 0 ? 'Sale placed — awaiting payment' : 'Sale open — add items or charge',
      tone: 'amber',
    }
  }

  return {
    state: 'staging',
    label: 'Staging',
    helper: 'Items not yet placed',
    tone: 'sky',
  }
}

export function orderPayableRemaining(order: Order | null | undefined): number {
  if (!order) return 0
  if (order.status === 'completed' || order.status === 'cancelled') return 0
  const paid =
    order.payments?.filter((p) => p.status === 'completed').reduce((s, p) => s + p.amount, 0) ?? 0
  return Math.max(0, order.total_amount - paid)
}

/**
 * Non-terminal statuses that belong on the counter "Open orders" strip.
 * Must stay in sync with the backend `orders_status_check` non-terminal set
 * (everything except 'completed'/'cancelled') so nothing the `open_only` fetch
 * returns is silently filtered back out. `out_for_delivery` (online COD
 * deliveries with the rider) still needs closing on return — the owner rule is
 * "open must always show" (audit/OPEN_ORDERS_STRIP_HIDDEN_2026-07-23.md).
 */
export const OPEN_ORDERS_STRIP_STATUSES = new Set(['pending', 'out_for_delivery'])

/**
 * Strip membership for Open Orders. Open = visible. Do not gate on payable
 * balance — a Rs 0 (fully comped) parked sale still needs closing
 * (audit/OPEN_ORDERS_STRIP_HIDDEN_2026-07-23.md).
 */
export function isOpenOrdersStripMember(
  order: { status?: string } | null | undefined,
): boolean {
  if (!order?.status) return false
  return OPEN_ORDERS_STRIP_STATUSES.has(order.status)
}

/**
 * Build the Open Orders strip for the active order type.
 *
 * Filters to non-terminal members of `openOrders` (caller should fetch with
 * open_only=true so completed sales cannot page them out), newest first.
 */
export function buildOpenOrdersStrip(openOrders: Order[], orderType: string): Order[] {
  const byId = new Map<string, Order>()
  for (const o of openOrders) {
    if (o.order_type !== orderType) continue
    if (!isOpenOrdersStripMember(o)) continue
    byId.set(o.id, o)
  }

  return [...byId.values()].sort((a, b) => {
    const ta = Date.parse(a.created_at) || 0
    const tb = Date.parse(b.created_at) || 0
    return tb - ta
  })
}

const ORDER_TYPE_PILL_BASE =
  'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide shadow-sm'

/** Order-type pill — tint from settings colors (matches checkout toggle). */
export function orderTypePillLook(orderTypeId: string, rows: OrderTypeConfig[]): {
  className: string
  style: CSSProperties
} {
  const hex = colorForOrderTypeId(rows, orderTypeId) ?? '#64748b'
  const h = sanitizeOrderTypeHexColor(hex, '#64748b')
  return {
    className: ORDER_TYPE_PILL_BASE,
    style: {
      borderColor: `color-mix(in srgb, ${h} 42%, hsl(var(--border)))`,
      backgroundColor: `color-mix(in srgb, ${h} 20%, hsl(var(--muted) / 0.35))`,
    },
  }
}

/** Tailwind classes for the lifecycle pill by tone. */
export function ticketPillClasses(tone: TicketLifecycleMeta['tone']): string {
  switch (tone) {
    case 'amber':
      return 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-100'
    case 'emerald':
      return 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100'
    case 'violet':
      return 'border-violet-300 bg-violet-50 text-violet-900 dark:border-violet-800 dark:bg-violet-900/30 dark:text-violet-100'
    case 'sky':
      return 'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-100'
    case 'slate':
      return 'border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200'
    case 'neutral':
    default:
      return 'border-border bg-muted/40 text-foreground'
  }
}

/**
 * Lifecycle pill — when tone is neutral, tint lightly with the active order type accent.
 */
export function ticketLifecyclePillLook(
  tone: TicketLifecycleMeta['tone'],
  orderTypeId: string,
  rows: OrderTypeConfig[]
): { className: string; style?: CSSProperties } {
  if (tone !== 'neutral') {
    return { className: cn(ORDER_TYPE_PILL_BASE, ticketPillClasses(tone)) }
  }
  const hex = colorForOrderTypeId(rows, orderTypeId) ?? '#64748b'
  const h = sanitizeOrderTypeHexColor(hex, '#64748b')
  return {
    className: ORDER_TYPE_PILL_BASE,
    style: {
      borderColor: `color-mix(in srgb, ${h} 34%, hsl(var(--border)))`,
      backgroundColor: `color-mix(in srgb, ${h} 11%, hsl(var(--muted) / 0.25))`,
    },
  }
}
